/* File: src/components/ProductTable.tsx */
import React, { useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { fetchTopProductsThunk } from "../../../../features/dashboard/dashboard.thunk";
import { selectTopProducts } from "../../../../features/dashboard/dashboard.selector";
import { default as DefaultImg } from "../../../../assets/default_product.png";
import { ITopProduct } from "../../../../features/dashboard/dashboard.states";

// Column metadata with formatting info and sorting configuration
interface HeaderDef {
  key: keyof ITopProduct;
  label: string;
  unit?: string;
  position?: "start" | "end";
  width?: string;
  sortable?: boolean;
}

const HEADERS: HeaderDef[] = [
  { key: "imageURL", label: "", width: "30px", sortable: false },
  {
    key: "costPrice",
    label: "Cost Price",
    unit: "$",
    position: "start",
    sortable: true,
  },
  {
    key: "salePrice",
    label: "Sale Price",
    unit: "$",
    position: "start",
    sortable: true,
  },
  {
    key: "profit",
    label: "Profit",
    unit: "$",
    position: "start",
    sortable: true,
  },
  {
    key: "profitPercentage",
    label: "Profit %",
    unit: "%",
    position: "end",
    sortable: true,
  },
  { key: "roi", label: "ROI", unit: "%", position: "end", sortable: true },
  { key: "bsr", label: "BSR", unit: "%", position: "end", sortable: true },
  {
    key: "estSales",
    label: "Estimated Sales",
    unit: "+",
    position: "end",
    sortable: true,
  },
];

// Styled components
const Container = styled(Paper)({
  borderRadius: 0,
  boxShadow: "none",
});

const Title = styled(Box)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 700,
  fontSize: 14,
  padding: "10px 12px",
}));

// Sorting types
type SortDirection = "asc" | "desc";

interface SortConfig {
  key: keyof ITopProduct | null;
  direction: SortDirection;
}

// Table header row with sorting functionality
interface TableHeaderProps {
  sortConfig: SortConfig;
  onRequestSort: (key: keyof ITopProduct) => void;
}

const TableHeader: React.FC<TableHeaderProps> = ({
  sortConfig,
  onRequestSort,
}) => {
  const createSortHandler = (property: keyof ITopProduct) => () => {
    onRequestSort(property);
  };

  return (
    <TableHead sx={{ position: "sticky", top: "0px" }}>
      <TableRow
        sx={{
          background: "rgba(238, 238, 238, 0.93) !important",
          height: { md: "24px", sm: "30px", xs: "30px" },
        }}
      >
        {HEADERS.map(({ key, label, width, sortable }, idx) => (
          <TableCell
            key={idx}
            align="center"
            sx={{
              color: "rgba(102, 102, 102, 0.8)",
              background: "#EEEEEEEE",
              fontSize: { md: "12px", sm: "10px" },
              fontWeight: 600,
              padding: 0,
              lineHeight: "12px",
              width,
            }}
          >
            {sortable && label ? (
              <TableSortLabel
                active={sortConfig.key === key}
                direction={
                  sortConfig.key === key ? sortConfig.direction : "asc"
                }
                onClick={createSortHandler(key)}
                sx={{
                  color: "rgba(102, 102, 102, 0.8) !important",
                  fontSize: 12,
                  fontWeight: 600,
                  "& .MuiTableSortLabel-icon": {
                    fontSize: 12,
                  },
                }}
              >
                {label}
              </TableSortLabel>
            ) : (
              label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

// Single product row
const ProductRow: React.FC<{ product: ITopProduct }> = React.memo(
  ({ product }) => (
    <TableRow
      hover
      sx={{
        borderBottom: "1px solid rgba(227, 227, 227, 1)",
        height: 29,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      {HEADERS.map(({ key, unit, position, width }) => {
        const value = product[key];
        const isImage = key === "imageURL";
        const highlight = ["profit", "profitPercentage", "roi"].includes(
          key as string,
        );
        return (
          <TableCell
            key={String(key)}
            align="center"
            sx={{ padding: 0, fontSize: 10, fontWeight: 600, width }}
          >
            {isImage ? (
              <Box
                component="img"
                src={value != null ? String(value) : DefaultImg}
                width={25}
                height={25}
              />
            ) : (
              <Box
                sx={{
                  color: highlight ? "rgba(4,118,71,1)" : "rgba(42,42,42,1)",
                }}
              >
                {position === "start" && unit}
                {typeof value === "number"
                  ? value.toLocaleString("en-US")
                  : value}
                {position === "end" && unit}
              </Box>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  ),
);

// Sorting utility functions
const getComparator = (sortConfig: SortConfig) => {
  return (a: ITopProduct, b: ITopProduct): number => {
    if (!sortConfig.key) return 0;

    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    // Handle different data types
    if (typeof aValue === "number" && typeof bValue === "number") {
      const result = aValue - bValue;
      return sortConfig.direction === "desc" ? -result : result;
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      const result = aValue.localeCompare(bValue);
      return sortConfig.direction === "desc" ? -result : result;
    }

    // Fallback comparison
    const result = String(aValue).localeCompare(String(bValue));
    return sortConfig.direction === "desc" ? -result : result;
  };
};

const sortedRowData = (
  array: ITopProduct[],
  comparator: (a: ITopProduct, b: ITopProduct) => number,
): ITopProduct[] => {
  return [...array].sort(comparator);
};

// Main component: fetches data and renders sortable table
const ProductTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { topProducts, loadingTopProducts } = useAppSelector(selectTopProducts);
  const [products, setProducts] = useState<ITopProduct[]>([]);
  // Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: "asc",
  });

  // Handle sort request
  const handleRequestSort = (key: keyof ITopProduct) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
    setSortConfig({
      key,
      direction: isAsc ? "desc" : "asc",
    });
  };

  // Sorted products based on current sort configuration
  const sortedProducts = useMemo(() => {
    if (!sortConfig.key) return products;
    return sortedRowData(products, getComparator(sortConfig));
  }, [products, sortConfig]);

  useEffect(() => {
    dispatch(fetchTopProductsThunk());
  }, [dispatch]);

  useEffect(() => {
    setProducts(topProducts);
  }, [topProducts]);

  return (
    <Container
      sx={{
        border: "1px solid rgba(227, 227, 227, 1)",
        borderRadius: "4px",
        position: "relative",
        flexGrow: 1,
      }}
    >
      <Title>Top Profitable Products</Title>
      {loadingTopProducts ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: 4,
            alignItems: "center",
            height: "460px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ height: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHeader
              sortConfig={sortConfig}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedProducts?.map((prod: ITopProduct, index: any) => (
                <ProductRow key={prod.asin + index} product={prod} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default ProductTable;
